import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@mui/material";
import { Download } from "@mui/icons-material";
import GenericRuleLogTable from "./GenericRuleLogTable";
import { getRuleLogs } from "../../API/ruleLogsApi";
import { exportMarginRuleLogs, exportSinglePriceLogs, getSinglePriceRows } from "../../API/marginRuleApi";
import AlertManager from "../../shared/Alert/Alert";
import classes from "./RulesPage.module.css";

const STRATEGY_LABELS = {
  gpneutral: "GP $ neutral",
  maintainprice: "Maintain price",
  maintainmargin: "Maintain margin",
  singleprice: "Single price",
};

const formatMarginStrategyLabel = (strategy) => {
  if (typeof strategy !== "string" || !strategy.trim()) {
    return "";
  }

  const normalizedStrategy = strategy.replace(/[^a-z0-9]+/gi, "").toLowerCase();
  if (STRATEGY_LABELS[normalizedStrategy]) {
    return STRATEGY_LABELS[normalizedStrategy];
  }

  return strategy
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

const formatEnabledValue = (value) => {
  if (value === true || value === 1 || value === "1" || value === "true" || value === "TRUE" || value === "yes" || value === "YES" || value === "y" || value === "Y") {
    return "Yes";
  }

  if (value === false || value === 0 || value === "0" || value === "false" || value === "FALSE" || value === "no" || value === "NO" || value === "n" || value === "N") {
    return "No";
  }

  return value ? String(value) : "";
};

const getMarginDimensionValue = (row, dimension) => {
  const rowScope = row?.scope?.toLowerCase();
  const dimensionFieldMap = {
    hq: ["hq", "hqLine"],
    category: ["category"],
    item: ["item", "itemSku"],
  };

  const fieldCandidates = dimensionFieldMap[dimension] || [dimension];

  for (const fieldName of fieldCandidates) {
    if (row?.[fieldName]) {
      return row[fieldName];
    }
  }

  if (rowScope === dimension) {
    if (dimension === "item") {
      return row?.itemSku || row?.scopeValue || "";
    }

    return row?.scopeValue || "";
  }

  return "";
};

export const MARGIN_COLUMNS = [
  {
    field: "category",
    headerName: "Category",
    flex: 0.5,
    minWidth: 90,
    valueGetter: ({ row }) => getMarginDimensionValue(row, "category"),
  },
  {
    field: "hq",
    headerName: "HQ",
    flex: 0.7,
    minWidth: 120,
    valueGetter: ({ row }) => getMarginDimensionValue(row, "hq"),
  },
  {
    field: "item",
    headerName: "Item",
    flex: 0.9,
    minWidth: 150,
    valueGetter: ({ row }) => getMarginDimensionValue(row, "item"),
  },
  {
    field: "marginStrategy",
    headerName: "Margin strategy",
    flex: 0.9,
    minWidth: 150,
    valueGetter: ({ row }) => {
      const strategy = row?.marginStrategy || row?.strategy || "";
      return formatMarginStrategyLabel(strategy);
    },
  },
  {
    field: "changedBy",
    headerName: "Set by",
    flex: 0.6,
    minWidth: 110,
    valueGetter: ({ row }) => row?.changedBy || row?.setBy || "",
  },
  {
    field: "changedDate",
    headerName: "Set Date",
    flex: 0.6,
    minWidth: 120,
    valueGetter: ({ row }) => row?.changedDate || row?.setDate || "",
  },
];

export const SINGLE_PRICE_COLUMNS = [
  {
    field: "sku",
    headerName: "SKU",
    flex: 0.9,
    minWidth: 140,
    valueGetter: ({ row }) => row?.sku || row?.parsedSku || row?.napaSku || row?.itemSku || "",
  },
  {
    field: "hqLine",
    headerName: "HQ LINE",
    flex: 0.8,
    minWidth: 120,
    valueGetter: ({ row }) => row?.hqLine || row?.hq_line || row?.hq || "",
  },
  {
    field: "singlePrice",
    headerName: "SINGLE PRICE",
    flex: 0.8,
    minWidth: 130,
    valueGetter: ({ row }) => row?.singlePrice ?? row?.price ?? row?.valueText ?? row?.value ?? "",
  },
  {
    field: "enabled",
    headerName: "ENABLED (YES/NO)",
    flex: 0.9,
    minWidth: 160,
    valueGetter: ({ row }) => formatEnabledValue(row?.enabled ?? row?.isEnabled ?? row?.active ?? row?.status),
  },
  {
    field: "setBy",
    headerName: "Set by",
    flex: 0.6,
    minWidth: 110,
    valueGetter: ({ row }) => row?.createdBy || row?.setBy || row?.changedBy || "",
  },
  {
    field: "setDate",
    headerName: "Set Date",
    flex: 0.6,
    minWidth: 120,
    valueGetter: ({ row }) => row?.createdDate || row?.setDate || row?.changedDate || "",
  },
];

export const getRuleLogColumns = (ruleType = "margin") =>
  ruleType === "singlePrice" ? SINGLE_PRICE_COLUMNS : MARGIN_COLUMNS;

const MarginRuleLogTable = ({
  refreshKey = 0,
  ruleType = "margin",
  rows,
  loading: externalLoading = false,
  page = 0,
  pageCount = 0,
  rowCount = 0,
  onPageChange,
  pageSize = 10,
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const isControlled = Array.isArray(rows);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = ruleType === "singlePrice" ? await getSinglePriceRows() : await getRuleLogs("margin");
      setLogs(data || []);
    } catch (error) {
      console.error("Error fetching margin rule logs:", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [ruleType]);

  useEffect(() => {
    if (isControlled) {
      setLogs(rows || []);
      setLoading(false);
      return;
    }

    void fetchLogs();
  }, [refreshKey, isControlled, rows, fetchLogs]);

  const displayedRows = isControlled ? rows || [] : logs;
  const displayedLoading = isControlled ? externalLoading : loading;
  const columns = getRuleLogColumns(ruleType);
  const paginationMode = ruleType === "singlePrice" ? "client" : "server";
  const isSinglePriceRule = ruleType === "singlePrice";

  const handleExportClick = async () => {
    try {
      if (isSinglePriceRule) {
        await exportSinglePriceLogs();
      } else {
        await exportMarginRuleLogs();
      }

      AlertManager.success(isSinglePriceRule ? "Single price logs exported successfully." : "Margin rule logs exported successfully.", {
        contactSupport: false,
      });
    } catch (error) {
      console.error(`Error exporting ${isSinglePriceRule ? "single price" : "margin rule"} logs:`, error);
      AlertManager.error(
        error?.message ||
          (isSinglePriceRule ? "Failed to export single price logs. Please try again." : "Failed to export margin rule logs. Please try again.")
      );
    }
  };

  return (
    <>
      <div className={classes.exportButtonContainer}>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExportClick}
          className={classes.exportButton}
        >
          Export all
        </Button>
      </div>
      <GenericRuleLogTable
        rows={displayedRows}
        columns={columns}
        loading={displayedLoading}
        paginationMode={paginationMode}
        page={paginationMode === "server" ? page : 0}
        pageCount={paginationMode === "server" ? pageCount : 0}
        rowCount={paginationMode === "server" ? rowCount : 0}
        pageSize={pageSize}
        onPageChange={paginationMode === "server" ? onPageChange : undefined}
      />
    </>
  );
};

export default MarginRuleLogTable;
